import React, { createContext, useState, useContext } from "react";
import { useAuth } from "./AuthContext";
import axiosInstance from "../api/axiosInstance";

const DataContext = createContext(null);

export const DataProvider = ({ children }) => {
  const { token } = useAuth();
  const [organizations, setOrganizations] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const [pagesFetched, setPagesFetched] = useState({
    organizations: {},
    invitations: {},
  });

  const [fetchingPages, setFetchingPages] = useState({
    organizations: {},
    invitations: {},
  });

  const fetchOrganizations = async (page = 1, size = 10) => {
    if (!token) return;

    if (pagesFetched.organizations[page]) return;

    if (fetchingPages.organizations[page]) return;

    setFetchingPages((prev) => ({
      ...prev,
      organizations: {
        ...prev.organizations,
        [page]: true,
      },
    }));

    setIsLoading(true);
    try {
      const response = await axiosInstance.get("/organizations/", {
        params: { page, size },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const newOrganizations = response.data.data || response.data;
      const total = response.data.totalPages || 1;

      if (page === 1) {
        setOrganizations(newOrganizations);
      } else {
        setOrganizations((prev) => [...prev, ...newOrganizations]);
      }

      setTotalPages(total);
      setError(null);

      setPagesFetched((prev) => ({
        ...prev,
        organizations: {
          ...prev.organizations,
          [page]: true,
        },
      }));
    } catch (err) {
      console.error("Error fetching organizations:", err);
      setError("Failed to fetch organizations");
    } finally {
      setIsLoading(false);
      setFetchingPages((prev) => ({
        ...prev,
        organizations: {
          ...prev.organizations,
          [page]: false,
        },
      }));
    }
  };

  const fetchInvitations = async (page = 1, size = 10) => {
    if (!token) return;

    if (pagesFetched.invitations[page]) return;

    if (fetchingPages.invitations[page]) return;

    setFetchingPages((prev) => ({
      ...prev,
      invitations: {
        ...prev.invitations,
        [page]: true,
      },
    }));

    setIsLoading(true);
    try {
      const response = await axiosInstance.get("/user/invitations", {
        params: { page, size },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const newInvitations = response.data.data || response.data;
      const total = response.data.totalPages || 1;

      if (page === 1) {
        setInvitations(newInvitations);
      } else {
        setInvitations((prev) => [...prev, ...newInvitations]);
      }

      setTotalPages(total);
      setError(null);

      setPagesFetched((prev) => ({
        ...prev,
        invitations: {
          ...prev.invitations,
          [page]: true,
        },
      }));
    } catch (err) {
      console.error("Error fetching invitations:", err);
      setError("Failed to fetch invitations");
    } finally {
      setIsLoading(false);
      setFetchingPages((prev) => ({
        ...prev,
        invitations: {
          ...prev.invitations,
          [page]: false,
        },
      }));
    }
  };

  const loadMoreInvitations = async () => {
    const nextPage = currentPage + 1;
    if (nextPage <= totalPages) {
      setCurrentPage(nextPage);
      await fetchInvitations(nextPage, pageSize);
    }
  };

  const loadMoreOrganizations = async () => {
    const nextPage = currentPage + 1;
    if (nextPage <= totalPages) {
      setCurrentPage(nextPage);
      await fetchOrganizations(nextPage, pageSize);
    }
  };

  const fetchFullInvitationData = async (size = 10) => {
    setPagesFetched({ organizations: {}, invitations: {} });
    setFetchingPages({ organizations: {}, invitations: {} });
    setCurrentPage(1);
    setPageSize(size);

    setIsLoading(true);
    await Promise.all([fetchOrganizations(1, size), fetchInvitations(1, size)]);
    setIsLoading(false);
  };

  const createInvitation = async (data) => {
    try {
      const response = await axiosInstance.post("/user/invitation", data, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.data.token) {
        const newInvitation = {
          token: response.data.token,
          orgid: data.orgid,
          role: data.role,
          max_uses: data.maxUses,
          times_used: 0,
          created_at: new Date().toISOString(),
          organization_name:
            organizations.find((org) => org.id === data.orgid)?.name || null,
          expires_at: response.data.expires_at,
          created_by: "You",
          id: Date.now(),
        };

        setInvitations((prev) => [newInvitation, ...prev]);

        return {
          success: true,
          data: {
            ...newInvitation,
            token: response.data.token,
          },
        };
      }

      return {
        success: false,
        message: "Failed to create invitation",
      };
    } catch (err) {
      console.error("Error creating invitation:", err);
      return {
        success: false,
        message: err.response?.data?.message || "Failed to create invitation",
      };
    }
  };

  const deleteInvitation = async (id) => {
    try {
      await axiosInstance.delete(`/user/invitations/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      setInvitations((prev) =>
        prev.filter((invitation) => invitation.id !== id)
      );

      return { success: true };
    } catch (err) {
      console.error("Error deleting invitation:", err);
      return {
        success: false,
        message: err.response?.data?.message || "Failed to delete invitation",
      };
    }
  };

  const createOrganization = async (data) => {
    try {
      const response = await axiosInstance.post("/organizations", data, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const newOrganization = response.data;

      setOrganizations((prev) => [newOrganization, ...prev]);

      return { success: true, data: newOrganization };
    } catch (err) {
      console.error("Error creating organization:", err);
      return {
        success: false,
        message: err.response?.data?.message || "Failed to create organization",
      };
    }
  };

  const updateOrganization = async (data) => {
    try {
      const response = await axiosInstance.put(
        `/organizations/${data.id}`,
        data,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const updatedOrganization = response.data;

      setOrganizations((prev) =>
        prev.map((org) =>
          org.id === updatedOrganization.id ? updatedOrganization : org
        )
      );

      return { success: true, data: updatedOrganization };
    } catch (err) {
      console.error("Error updating organization:", err);
      return {
        success: false,
        message: err.response?.data?.message || "Failed to update organization",
      };
    }
  };

  const resetDataState = () => {
    setOrganizations([]);
    setInvitations([]);
    setPagesFetched({ organizations: {}, invitations: {} });
    setFetchingPages({ organizations: {}, invitations: {} });
    setCurrentPage(1);
    setTotalPages(1);
    setError(null);
  };

  const value = {
    organizations,
    invitations,
    isLoading,
    error,
    currentPage,
    totalPages,
    pageSize,
    pagesFetched,
    fetchOrganizations,
    fetchInvitations,
    loadMoreInvitations,
    loadMoreOrganizations,
    fetchFullInvitationData,
    createInvitation,
    deleteInvitation,
    createOrganization,
    updateOrganization,
    resetDataState,
    setPageSize,
  };

  return <DataContext.Provider value={value}>{children}</DataContext.Provider>;
};

export const useData = () => {
  const context = useContext(DataContext);
  if (!context) {
    throw new Error("useData must be used within a DataProvider");
  }
  return context;
};
